import type { CSSProperties, ReactNode } from "react";
import type { CreditPool, RealmId, UserProfile } from "@chuzi/shared/types";
import { t } from "@chuzi/shared/config";
import { useSpendingModal } from "../lib/spending-modal";

interface AddCreditsButtonProps {
  realm: RealmId;
  pool: CreditPool;
  creditsNeeded?: number;
  canAfford: boolean;
  user?: UserProfile | null;
  label: string;
  disabled?: boolean;
  onAction: () => void;
  onCreditsAdded?: () => void;
  style?: CSSProperties;
  children?: ReactNode;
  variant?: "default" | "hero";
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
}

function creditsPromptColor(realm: RealmId): string {
  return realm === "wilds" ? "#c8f0a0" : "#ffd080";
}

export function AddCreditsButton({
  realm,
  pool,
  creditsNeeded = 1,
  canAfford,
  user = null,
  label,
  disabled = false,
  onAction,
  onCreditsAdded,
  style,
  children,
  variant = "default",
  onMouseEnter,
  onMouseLeave,
}: AddCreditsButtonProps) {
  const { openSpendingModal } = useSpendingModal();
  const needsCredits = !canAfford && !user?.is_admin;
  const isHero = variant === "hero";

  function handleClick() {
    if (needsCredits) {
      openSpendingModal({
        pool,
        creditsNeeded,
        onSuccess: onCreditsAdded,
      });
      return;
    }
    onAction();
  }

  const promptColor = creditsPromptColor(realm);

  const content = children ?? (
    needsCredits ? (
      <span
        style={{
          display: "inline-flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: isHero ? "6px 10px" : "4px 6px",
          textAlign: "center",
          lineHeight: 1.35,
          padding: isHero ? "0 10px" : 0,
        }}
      >
        <span
          style={{
            textDecoration: "line-through",
            opacity: isHero ? 0.22 : 0.4,
            color: isHero ? "rgba(200,180,220,0.45)" : undefined,
            fontSize: isHero ? 13 : undefined,
            fontWeight: isHero ? 500 : undefined,
          }}
        >
          {label}
        </span>
        <span
          className="chuzi-credits-breathe"
          style={{
            color: promptColor,
            fontWeight: 700,
            fontSize: isHero ? 15 : undefined,
            ["--chuzi-credits-glow" as string]: promptColor,
          }}
        >
          {t(realm, "spending_add_credits_continue")}
        </span>
      </span>
    ) : (
      label
    )
  );

  return (
    <>
      {needsCredits ? (
        <style>{`
          @keyframes chuzi-credits-breathe {
            0%, 100% {
              opacity: 0.82;
              text-shadow: 0 0 6px color-mix(in srgb, var(--chuzi-credits-glow) 45%, transparent);
            }
            50% {
              opacity: 1;
              text-shadow:
                0 0 10px color-mix(in srgb, var(--chuzi-credits-glow) 85%, transparent),
                0 0 22px color-mix(in srgb, var(--chuzi-credits-glow) 40%, transparent);
            }
          }
          .chuzi-credits-breathe {
            animation: chuzi-credits-breathe 2.4s ease-in-out infinite;
          }
        `}</style>
      ) : null}
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={disabled && !needsCredits}
        style={{
          ...style,
          ...(isHero
            ? {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }
            : {}),
        }}
      >
        {content}
      </button>
    </>
  );
}
